/**
 * JobsList — Cockpit skin
 * ───────────────────────
 * Same data, same actions, same routes as JobsList. Restructured into the
 * Cockpit visual language: masthead w/ KPI strip, hairline filter bar,
 * hairline list (no card chrome), tabular-nums everywhere.
 */
import { useAppData } from "@/data/AppDataContext";
import { useJobsQuery } from "@/hooks/useJobsQuery";
import { JobService } from "@/services/job.service";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { ArrowRight, CheckCircle2, AlertTriangle, PackageX, FileWarning,
  Users, ClipboardCheck, AlertCircle, Clock } from "lucide-react";
import { usePagination } from "@/hooks/usePagination";
import { useProjectJobBranchRegionOptions, useProjectJobsPage, useProjectJobsSummary } from "@/hooks/useProjectServerPaging";
import { PaginationBar } from "@/components/PaginationBar";
import { getJobBlockers, type Blocker } from "@/data/workflowBlockers";
import { getJobNextAction } from "@/data/nextActionEngine";
import { getLastActivity, formatRelativeTime, type SuggestedAction } from "@/data/suggestedActions";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import { formatAddress } from "@/lib/addressUtils";
import { OrderingModal } from "@/components/OrderingModal";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { usePushChain } from "@/hooks/usePushChain";
import { toast } from "sonner";
import type { Job } from "@/data/types";
import { PermissionGate } from "@/components/PermissionGate";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitTabs,
  CockpitSearch, CockpitListShell, CockpitColumns, CockpitRow, CockpitEmpty,
} from "@/components/cockpit/CockpitShell";
import { ReadinessBadge } from "@/components/ReadinessBadge";
import { cn } from "@/lib/utils";
import { PROJECTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";

interface JobProgress {
  percent: number;
  label: string;
  status: "blocked" | "in_progress" | "ready" | "completed";
}

function calculateJobProgress(job: Job, blockers: Blocker[]): JobProgress {
  if (job.currentStage === "Completed") return { percent: 100, label: "Completed", status: "completed" };
  const permitDone = !job.permitRequired || job.permitStatus === "Approved";
  const materialsDone = job.materialReadiness === "Fully Received" || job.materialReadiness === "Loaded for Crew";
  const crewDone = job.assignmentType !== "Unassigned";
  const qaDone = !!job.qaSubmitted;
  const doneCount = [permitDone, materialsDone, crewDone, qaDone].filter(Boolean).length;
  const percent = Math.round((doneCount / 4) * 100);
  const hasCritical = blockers.some(b => b.severity === "critical");
  if (hasCritical) return { percent, label: "Blocked", status: "blocked" };
  if (percent === 100) return { percent, label: "Ready", status: "ready" };
  return { percent, label: percent >= 75 ? "Almost ready" : "In progress", status: "in_progress" };
}

const ACTION_ICON: Record<string, typeof ArrowRight> = {
  crew: Users, materials: PackageX, permit: FileWarning,
  schedule: Clock, dispatch: ArrowRight, issue: AlertTriangle,
  qa: ClipboardCheck, closeout: CheckCircle2,
};

interface JobsListCockpitProps {
  embedded?: boolean;
  headerTargetId?: string;
  summaryJobs?: Job[];
  summaryLoading?: boolean;
}

export default function JobsListCockpit({
  embedded,
  summaryJobs: externalSummaryJobs,
  summaryLoading: externalSummaryLoading = false,
}: JobsListCockpitProps = {}) {
  const { issues, setJobs: setGlobalJobs } = useAppData();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("active");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const [serverPage, setServerPage] = useState(0);
  const [serverPageSize, setServerPageSize] = useState(50);
  const serverPaginationEnabled = PROJECTS_SERVER_PAGING_ENABLED && !!embedded;
  const serverJobs = useProjectJobsPage({
    enabled: serverPaginationEnabled,
    tab: tab as any,
    page: serverPage,
    pageSize: serverPageSize,
    search,
    branch: brFilter.branch,
    region: brFilter.region,
  });
  const serverSummary = useProjectJobsSummary({
    enabled: serverPaginationEnabled,
    branch: brFilter.branch,
    region: brFilter.region,
  });
  const serverBranchRegionOptions = useProjectJobBranchRegionOptions({
    enabled: serverPaginationEnabled,
  });
  const {
    jobs: fallbackJobs,
    setJobs: setFallbackPageJobs,
    loading: fallbackLoading,
    totalCount: fallbackTotalCount,
  } = useJobsQuery({
    view: "all",
    search: "",
    enabled: !serverPaginationEnabled,
  });
  const jobs = serverPaginationEnabled ? serverJobs.jobs : fallbackJobs;
  const setPageJobs = serverPaginationEnabled ? serverJobs.setJobs : setFallbackPageJobs;
  const loading = serverPaginationEnabled ? serverJobs.loading : fallbackLoading;
  const totalCount = serverPaginationEnabled ? serverJobs.count : fallbackTotalCount;

  useEffect(() => {
    setServerPage(0);
  }, [brFilter.branch, brFilter.region, search, serverPageSize, tab]);

  const findOpenIssueForJob = useCallback((jobId: string) => {
    const oi = issues.filter(i => i.jobId === jobId && (i.status === "Open" || i.status === "In Progress"));
    return oi.sort((a, b) => (a.severity === "Urgent" ? -1 : 1) - (b.severity === "Urgent" ? -1 : 1))[0] ?? null;
  }, [issues]);

  const chain = usePushChain({ navigate, findOpenIssueForJob });

  const handleActionClick = useCallback(async (job: Job, action: SuggestedAction) => {
    let executable: ExecutableAction;
    if (action.executionMode === "modal" && action.icon === "materials") {
      executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => chain.openOrdering(job) };
    } else if (action.executionMode === "modal" && action.icon === "schedule") {
      executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => chain.openReschedule(job) };
    } else if (action.executionMode === "modal" && (action.icon === "dispatch" || action.icon === "crew")) {
      executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => chain.openAssign(job) };
    } else if (action.executionMode === "modal" && action.icon === "issue") {
      executable = {
        label: action.cta || action.label, executionMode: "modal",
        openModal: () => {
          const top = findOpenIssueForJob(job.id);
          if (top) chain.openResolve(top); else toast.info("No open issue to resolve");
        },
      };
    } else {
      executable = { label: action.cta || action.label, executionMode: "navigate", route: action.link || `/jobs/${job.id}` };
    }
    const handled = await runAction(executable, { navigate });
    if (!handled && action.link) navigate(action.link);
  }, [navigate, chain, findOpenIssueForJob]);

  const handleOrderingSubmit = useCallback(async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
    const target = jobs.find((j) => j.id === data.jobId);
    if (!target) return;
    const patch: Partial<Job> = {
      materialReadiness: "Ordered" as any,
      planningStatus: "Ordered",
      selectedVendor: data.vendor,
      activityHistory: [...target.activityHistory, {
        id: `jobs-order-${Date.now()}`,
        date: new Date().toISOString(),
        action: `Materials ordered — ${data.vendor} (${data.orderType})`,
        user: "Ops", noteType: "ops" as const,
      }],
    };
    const { data: persisted, error } = await JobService.update(target.id, patch);
    if (error || !persisted) {
      console.error("Failed to persist ordering update:", error);
      toast.error("Failed to place order");
      return;
    }
    setGlobalJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
    setPageJobs(prev => prev.map(j => (j.id === persisted.id ? persisted : j)));
    toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
  }, [jobs, setGlobalJobs, setPageJobs]);

  const brFiltered = serverPaginationEnabled ? jobs : filterByBranchRegion(jobs, brFilter);
  const summarySourceJobs = serverPaginationEnabled
    ? externalSummaryJobs ?? []
    : jobs;
  const summaryLoading = serverPaginationEnabled
    ? externalSummaryLoading || serverSummary.loading
    : false;
  const summaryBrFiltered = serverPaginationEnabled ? summarySourceJobs : filterByBranchRegion(summarySourceJobs, brFilter);

  const jobData = useMemo(() => {
    const map = new Map<string, { openIssues: typeof issues; blockers: Blocker[]; progress: JobProgress; nextAction: { action: SuggestedAction; icon: typeof ArrowRight } | null }>();
    brFiltered.forEach(j => {
      const oi = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      const blockers = j.currentStage !== "Completed" ? getJobBlockers(j, oi) : [];
      const progress = calculateJobProgress(j, blockers);
      const a = j.currentStage === "Completed" ? null : getJobNextAction(j, oi);
      const nextAction = a ? { action: a, icon: ACTION_ICON[a.icon] || ArrowRight } : null;
      map.set(j.id, { openIssues: oi, blockers, progress, nextAction });
    });
    return map;
  }, [brFiltered, issues]);

  const summaryJobData = useMemo(() => {
    const map = new Map<string, { blockers: Blocker[]; progress: JobProgress }>();
    summaryBrFiltered.forEach(j => {
      const oi = issues.filter(i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
      const blockers = j.currentStage !== "Completed" ? getJobBlockers(j, oi) : [];
      const progress = calculateJobProgress(j, blockers);
      map.set(j.id, { blockers, progress });
    });
    return map;
  }, [summaryBrFiltered, issues]);

  const rowActive = brFiltered.filter(j => j.currentStage !== "Completed");
  const rowBlocked = rowActive.filter(j => jobData.get(j.id)?.progress.status === "blocked");
  const rowReady = rowActive.filter(j => jobData.get(j.id)?.progress.status === "ready");
  const rowNeedsAttention = rowActive.filter(j => {
    const d = jobData.get(j.id);
    return d && d.blockers.some(b => b.severity === "critical");
  });

  const active = summaryBrFiltered.filter(j => j.currentStage !== "Completed");
  const blocked = active.filter(j => summaryJobData.get(j.id)?.progress.status === "blocked");
  const ready = active.filter(j => summaryJobData.get(j.id)?.progress.status === "ready");
  const needsAttention = active.filter(j => {
    const d = summaryJobData.get(j.id);
    return d && d.blockers.some(b => b.severity === "critical");
  });
  const completed = summaryBrFiltered.filter(j => j.currentStage === "Completed");
  const atRisk = active.filter(j => {
    const p = summaryJobData.get(j.id)?.progress;
    return p && p.percent > 0 && p.percent < 75 && p.status !== "blocked";
  }).length;
  const pctReady = active.length > 0 ? Math.round((ready.length / active.length) * 100) : 0;

  const summaryCounts = serverPaginationEnabled ? serverSummary.data : null;
  const activeCount = summaryCounts?.active ?? active.length;
  const attentionCount = summaryCounts?.attention ?? needsAttention.length;
  const blockedCount = summaryCounts?.blocked ?? blocked.length;
  const readyCount = summaryCounts?.ready ?? ready.length;
  const completedCount = summaryCounts?.completed ?? completed.length;
  const allCount = summaryCounts?.all ?? summaryBrFiltered.length;
  const atRiskCount = summaryCounts?.atRisk ?? atRisk;
  const pctReadyDisplay = summaryCounts?.pctReady ?? pctReady;

  const tabs = [
    { value: "active", label: "Active", count: activeCount },
    { value: "attention", label: "Attention", count: attentionCount },
    { value: "blocked", label: "Blocked", count: blockedCount },
    { value: "ready", label: "Ready", count: readyCount },
    { value: "completed", label: "Completed", count: completedCount },
    { value: "all", label: "All", count: allCount },
  ];

  const viewFiltered = serverPaginationEnabled ? brFiltered : brFiltered.filter(j => {
    switch (tab) {
      case "active": return j.currentStage !== "Completed";
      case "attention": return rowNeedsAttention.includes(j);
      case "blocked": return rowBlocked.includes(j);
      case "ready": return rowReady.includes(j);
      case "completed": return j.currentStage === "Completed";
      default: return true;
    }
  });

  const filtered = serverPaginationEnabled ? viewFiltered : viewFiltered.filter(j =>
    j.customerName.toLowerCase().includes(search.toLowerCase()) ||
    j.id.toLowerCase().includes(search.toLowerCase()) ||
    (j.jobNumber || "").toLowerCase().includes(search.toLowerCase()) ||
    j.projectType.toLowerCase().includes(search.toLowerCase()) ||
    j.currentStage.toLowerCase().includes(search.toLowerCase())
  );

  const localPagination = usePagination(filtered, 50);
  useEffect(() => { localPagination.reset(); }, [search, tab]);

  const serverTotalPages = Math.max(1, Math.ceil(totalCount / serverPageSize));
  const safeServerPage = Math.min(serverPage, serverTotalPages - 1);
  useEffect(() => {
    if (serverPaginationEnabled && serverPage !== safeServerPage) {
      setServerPage(safeServerPage);
    }
  }, [safeServerPage, serverPage, serverPaginationEnabled]);

  const pagination = serverPaginationEnabled
    ? {
        paged: filtered,
        page: safeServerPage,
        totalPages: serverTotalPages,
        totalItems: totalCount,
        hasNext: safeServerPage < serverTotalPages - 1,
        hasPrev: safeServerPage > 0,
        next: () => setServerPage((p) => Math.min(p + 1, serverTotalPages - 1)),
        prev: () => setServerPage((p) => Math.max(p - 1, 0)),
        reset: () => setServerPage(0),
        pageStart: totalCount > 0 ? safeServerPage * serverPageSize + 1 : 0,
        pageEnd: Math.min((safeServerPage + 1) * serverPageSize, totalCount),
        pageSize: serverPageSize,
        setPageSize: (size: number) => {
          setServerPageSize(size);
          setServerPage(0);
        },
      }
    : localPagination;

  const template = "0.7fr 1.6fr 1.2fr 0.9fr 1.4fr 1.1fr 0.9fr";

  const body = (
    <>
      <CockpitTabs tabs={tabs} value={tab} onChange={setTab} />

      <div className="flex flex-wrap items-center gap-3">
        <CockpitSearch value={search} onChange={setSearch} placeholder="Search jobs, customers, project type…" />
        <div className="ml-auto">
          <BranchRegionFilter
            value={brFilter}
            onChange={setBrFilter}
            jobs={summarySourceJobs}
            branches={serverPaginationEnabled ? serverBranchRegionOptions.data?.branches : undefined}
            regions={serverPaginationEnabled ? serverBranchRegionOptions.data?.regions : undefined}
            compact
          />
        </div>
      </div>

      {loading && jobs.length === 0 ? (
        <CockpitEmpty message="Loading jobs…" />
      ) : filtered.length === 0 ? (
        <CockpitEmpty message={summaryLoading ? "Loading project summary…" : "No jobs match your filters."} />
      ) : (
        <CockpitListShell>
          <CockpitColumns template={template}>
            {["Job", "Customer", "Stage", "Progress", "Next action", "Assigned", "Last activity"].map(c => (
              <span key={c}>{c}</span>
            ))}
          </CockpitColumns>
          {pagination.paged.map(j => {
            const d = jobData.get(j.id);
            if (!d) return null;
            const { progress, nextAction } = d;
            const last = getLastActivity(j);
            const formattedAddress = formatAddress(j.address);
            return (
              <CockpitRow key={j.id} template={template} onClick={() => navigate(`/jobs/${j.id}`)}>
                <span className="font-medium tabular-nums text-foreground">{j.jobNumber || j.id}</span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate font-medium">{j.customerName}</span>
                    <ReadinessBadge job={j} />
                  </span>
                  <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={formattedAddress}
                  >
                    {formattedAddress}
                  </span>
                </span>
                <span className="text-muted-foreground truncate">{j.currentStage}</span>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="h-1 flex-1 max-w-[80px] rounded-full bg-muted overflow-hidden">
                    <span
                      className={cn(
                        "block h-full",
                        progress.status === "blocked" ? "bg-destructive" :
                        progress.status === "ready" ? "bg-success" :
                        progress.status === "completed" ? "bg-success" : "bg-foreground/70",
                      )}
                      style={{ width: `${progress.percent}%` }}
                    />
                  </span>
                  <span className="tabular-nums text-[11px] text-muted-foreground shrink-0">{progress.percent}%</span>
                </span>
                <span>
                  {nextAction ? (
                    <PermissionGate permission="jobs_edit">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleActionClick(j, nextAction.action); }}
                        className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors text-xs font-medium truncate max-w-full"
                        title={nextAction.action.description}
                      >
                        <nextAction.icon className="h-3 w-3 shrink-0 text-muted-foreground" />
                        <span className="truncate">{nextAction.action.cta || nextAction.action.label}</span>
                      </button>
                    </PermissionGate>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-success text-xs">
                      <CheckCircle2 className="h-3 w-3" /> On track
                    </span>
                  )}
                </span>
                <span className="text-muted-foreground truncate">{j.assignedCrew || j.assignedSubcontractor || "—"}</span>
                <span className="text-muted-foreground tabular-nums text-[11px]">
                  {last ? formatRelativeTime(last.date) : "—"}
                </span>
              </CockpitRow>
            );
          })}
        </CockpitListShell>
      )}

      <PaginationBar {...pagination} />

      {/* Push-chain modals — same wiring as classic JobsList */}
      {chain.ordering && (
        <OrderingModal
          open={!!chain.ordering}
          onOpenChange={(open) => { if (!open) chain.closeAll(); }}
          job={chain.ordering}
          openIssues={issues.filter(i => i.jobId === chain.ordering!.id && (i.status === "Open" || i.status === "In Progress"))}
          onSendToOrdering={handleOrderingSubmit}
          onChooseNextStep={(step) => {
            const j = jobs.find(x => x.id === chain.ordering!.id) ?? chain.ordering!;
            chain.chainTo(step, { ...j, materialReadiness: "Ordered" as Job["materialReadiness"] });
          }}
        />
      )}
      <RescheduleModal
        open={!!chain.reschedule}
        onOpenChange={(open) => { if (!open) chain.closeAll(); }}
        job={chain.reschedule}
        openIssues={chain.reschedule ? issues.filter(i => i.jobId === chain.reschedule!.id && (i.status === "Open" || i.status === "In Progress")) : []}
        onChooseNextStep={(step, updatedJob) => chain.chainTo(step, updatedJob)}
      />
      <QuickAssignModal
        open={!!chain.assign}
        onOpenChange={(open) => { if (!open) chain.closeAll(); }}
        job={chain.assign}
        openIssues={chain.assign ? issues.filter(i => i.jobId === chain.assign!.id && (i.status === "Open" || i.status === "In Progress")) : []}
        onChooseNextStep={(step, updatedJob) => chain.chainTo(step, updatedJob)}
      />
      <QuickResolveIssueModal
        open={!!chain.resolve}
        onOpenChange={(open) => { if (!open) chain.closeAll(); }}
        issue={chain.resolve}
        onChooseNextStep={(step, job) => chain.chainTo(step, job)}
      />
    </>
  );

  if (embedded) return <div className="flex flex-col gap-4 pt-2">{body}</div>;

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Jobs"
        right={
          <>
        <HeroMetric label="Active" value={activeCount} hero />
        <HeroDivider />
        <HeroMetric label="Ready" value={`${pctReadyDisplay}%`} tone={pctReadyDisplay >= 50 ? "good" : "default"} />
        <HeroMetric label="Blocked" value={blockedCount} tone={blockedCount > 0 ? "bad" : "default"} />
        <HeroMetric label="At risk" value={atRiskCount} tone={atRiskCount > 0 ? "warn" : "default"} />
          </>
        }
      />
      {body}
    </CockpitPage>
  );
}
